<script setup lang="ts">
import { ref, reactive, StyleValue, watchEffect, watch } from "vue";
import { Timeline, Easing, hasClass } from "@zyzgroup/core-web";
import Overlay from "./Overlay.vue";
import Box from "../base/Box.vue";

// for merge ce shadow-root styles
defineOptions({
  customOptions: { Overlay, Box },
  inheritAttrs: true
});

// 需传入 key 使子元素重新创建，从而使浏览器计算 popContentStyle inset 正确
const props = withDefaults(
  defineProps<{
    closeWhenClickOverlay?: boolean;
    directionFrom?:
      | "bottom"
      | "top"
      | "left"
      | "right"
      | "center"
      | "absolute"
      | "full";
    dur?: number;
    global?: boolean;
    overlayBgColor?: string;
    contentBgColor?: string;
    fixed?: number; // fixed 单位px, 强制设置弹起高度或宽度, 如果不传，默认 slot width or height
    popLeft?: string; // absolute left position
    popTop?: string; // absolute top position
  }>(),
  {
    closeWhenClickOverlay: true,
    directionFrom: "bottom",
    dur: 200,
    global: true,
    popLeft: "0%",
    popTop: "0%",
    contentBgColor: "rgba(255,255,255,0)"
  }
);

const emits = defineEmits<{
  (event: "open"): void;
  (event: "close"): void;
  (event: "resize", rect: DOMRect): void;
  (event: "clickOverlay"): void;
}>();

const overlayRef = ref();

const popContentRef = ref();
const popContentStyle = reactive<{
  // 特性属性
  width?: string;
  height?: string;
  top?: string;
  bottom?: string;
  left?: string;
  right?: string;
  transform?: string;
  // 通用属性
  display: string;
  position: string;
  opacity?: String;
  transformOrigin?: string;
  willChange?: string; // 优化动画性能, 让浏览器提前为动画做准备，避免抖动或延迟
  backgroundColor?: string;
  inset?: string; // top right bottom left
}>({
  display: "block",
  position: "absolute",
  opacity: "1",
  transformOrigin: "center",
  willChange: "transform",
  backgroundColor: props.contentBgColor
});

const tl = new Timeline({ frameDuration: props.dur });

const boxStyle = reactive<{
  width?: string;
  height?: string;
}>({});

// Overlay open() close() 会改变 Box.resize
let boxWidth = 0;
let boxHeight = 0;
const boxResize = (box) => {
  // box.rect.x 或 box.rect.y 变化时不触发
  if (boxWidth != box.rect.width || boxHeight != box.rect.height) {
    boxWidth = box.rect.width;
    boxHeight = box.rect.height;
    if (action == "open") {
      computePopValue(isShow);
      tl.play();
    }
  }
};

watchEffect(() => {
  switch (props.directionFrom) {
    case "left":
    case "right":
      delete boxStyle.width;
      boxStyle.height = "100%";
      break;
    case "top":
    case "bottom":
      boxStyle.width = "100%";
      delete boxStyle.height;
      break;
    case "full":
      boxStyle.width = "100%";
      boxStyle.height = "100%";
      break;
    case "center":
    case "absolute":
      delete boxStyle.width;
      delete boxStyle.height;
      break;
  }
});

watch(
  () => [props.popLeft, props.popTop],
  () => {
    if (props.directionFrom == "absolute") {
      computePopValue(true);
    }
  }
);

const popUnit = "px";
let update = false;
let lastPopValue;

const computePopValue = (_update = false) => {
  tl.clear();

  update = _update;

  let diffValue;
  switch (props.directionFrom) {
    case "left":
      diffValue = update
        ? props.fixed
          ? 0
          : lastPopValue - boxWidth
        : props.fixed
        ? props.fixed
        : boxWidth;
      lastPopValue = props.fixed ? props.fixed : boxWidth;
      popContentStyle.width = `${lastPopValue}${popUnit}`;
      popContentStyle.height = "100%";
      popContentStyle.top = "0";
      popContentStyle.bottom = "0";
      delete popContentStyle.right;
      popContentStyle.left = `-${diffValue}${popUnit}`;
      if (diffValue) {
        tl.addTween(
          {
            left: diffValue
          },
          { left: 0 },
          {
            easeFunction: Easing.Quadratic.Out,
            onUpdate(data) {
              popContentStyle.left = `-${data.left}${popUnit}`;
            },
            onComplete
          }
        );
      }
      break;
    case "right":
      diffValue = update
        ? props.fixed
          ? 0
          : lastPopValue - boxWidth
        : props.fixed
        ? props.fixed
        : boxWidth;
      lastPopValue = props.fixed ? props.fixed : boxWidth;
      popContentStyle.width = `${lastPopValue}${popUnit}`;
      popContentStyle.height = "100%";
      popContentStyle.top = "0";
      popContentStyle.bottom = "0";
      delete popContentStyle.left;
      popContentStyle.right = `-${diffValue}${popUnit}`;
      if (diffValue) {
        tl.addTween(
          {
            right: diffValue
          },
          { right: 0 },
          {
            easeFunction: Easing.Quadratic.Out,
            onUpdate(data) {
              popContentStyle.right = `-${data.right}${popUnit}`;
            },
            onComplete
          }
        );
      }
      break;
    case "top":
      diffValue = update
        ? props.fixed
          ? 0
          : lastPopValue - boxHeight
        : props.fixed
        ? props.fixed
        : boxHeight;
      lastPopValue = props.fixed ? props.fixed : boxHeight;
      popContentStyle.width = "100%";
      popContentStyle.height = `${lastPopValue}${popUnit}`;
      popContentStyle.left = "0";
      popContentStyle.right = "0";
      delete popContentStyle.bottom;
      popContentStyle.top = `-${diffValue}${popUnit}`;
      if (diffValue) {
        tl.addTween(
          {
            top: diffValue
          },
          { top: 0 },
          {
            easeFunction: Easing.Quadratic.Out,
            onUpdate(data) {
              popContentStyle.top = `-${data.top}${popUnit}`;
            },
            onComplete
          }
        );
      }
      break;
    case "bottom":
      diffValue = update
        ? props.fixed
          ? 0
          : lastPopValue - boxHeight
        : props.fixed
        ? props.fixed
        : boxHeight;
      lastPopValue = props.fixed ? props.fixed : boxHeight;
      popContentStyle.width = "100%";
      popContentStyle.height = `${lastPopValue}${popUnit}`;
      popContentStyle.left = "0";
      popContentStyle.right = "0";
      delete popContentStyle.top;
      popContentStyle.bottom = `-${diffValue}${popUnit}`;
      if (diffValue) {
        tl.addTween(
          {
            bottom: diffValue
          },
          { bottom: 0 },
          {
            easeFunction: Easing.Quadratic.Out,
            onUpdate(data) {
              popContentStyle.bottom = `-${data.bottom}${popUnit}`;
            },
            onComplete
          }
        );
      }
      break;
    case "center":
      if (!update) {
        delete popContentStyle.width;
        delete popContentStyle.height;
        delete popContentStyle.right;
        delete popContentStyle.bottom;
        popContentStyle.left = "50%";
        popContentStyle.top = "50%";
        popContentStyle.transform = `translate(-50%, -50%) scale(${0.8})`;
        popContentStyle.opacity = `0`;
        tl.addTween(
          {
            scale: 0.8,
            opacity: 0
          },
          {
            scale: 1,
            opacity: 1
          },
          {
            easeFunction: Easing.Quadratic.Out,
            onUpdate(data) {
              popContentStyle.transform = `translate(-50%, -50%) scale(${data.scale})`;
              popContentStyle.opacity = `${data.opacity}`;
            },
            onComplete
          }
        );
      }
      break;
    case "absolute":
      popContentStyle.width = boxWidth + popUnit;
      popContentStyle.height = boxHeight + popUnit;
      delete popContentStyle.right;
      delete popContentStyle.bottom;
      popContentStyle.left = props.popLeft;
      popContentStyle.top = props.popTop;
      onComplete();
      break;
    case "full":
      popContentStyle.width = "100%";
      popContentStyle.height = "100%";
      delete popContentStyle.right;
      delete popContentStyle.bottom;
      popContentStyle.left = "0%";
      popContentStyle.top = "0%";
      onComplete();
      break;
  }

  switch (props.directionFrom) {
    case "center":
      break;
    default:
      delete popContentStyle.transform;
      popContentStyle.opacity = "1";
      break;
  }
};

let action: "open" | "close" = "open";
let isShow = false;

function onComplete() {
  if (!update) {
    switch (action) {
      case "open":
        isShow = true;
        emits("open");
        break;
      case "close":
        isShow = false;
        overlayRef.value?.close();
        emits("close");
        break;
    }
  }
  // resize 顺序很重要！
  if (popContentRef.value) {
    emits("resize", popContentRef.value.getBoundingClientRect());
  }
}

const popOpen = () => {
  switch (props.directionFrom) {
    case "full":
      computePopValue();
      break;
  }
};

function open() {
  action = "open";
  update = false;
  overlayRef.value?.open();
  // 等待 resize 执行 或 popOpen
}

function close() {
  action = "close";
  update = false;
  switch (props.directionFrom) {
    case "absolute":
    case "full":
      onComplete();
      break;
    default:
      computePopValue();
      tl.reverse();
      break;
  }
}

function clickOverlay(e: Event) {
  // console.log(e.target);
  // console.log(popRef.value.contains(e.target));
  const className =
    props.directionFrom == "full"
      ? // ||
        // (props.directionFrom == "absolute" &&
        //   props.popLeft == "0%" &&
        //   props.popTop == "0%") // 默认 boxWidth==100% boxHeight==100%
        "pop-content"
      : "pop";
  if (e.target && hasClass(e.target as HTMLElement, className)) {
    if (props.closeWhenClickOverlay) {
      close();
    }
    emits("clickOverlay");
  }
}

defineExpose({ open, close });
</script>

<template>
  <Overlay
    ref="overlayRef"
    class="pop"
    :global="props.global"
    :color="props.overlayBgColor"
    @open="popOpen"
    @click="clickOverlay"
  >
    <div
      ref="popContentRef"
      class="pop-content"
      :style="popContentStyle as StyleValue"
    >
      <Box
        displayType="shrink"
        :style="boxStyle"
        @resize="boxResize"
      >
        <slot></slot>
      </Box>
    </div>
  </Overlay>
</template>
